// File: src/POAInstructionsPage.js
// POA Instructions Gateway - Shows licence address and lets user declare address mismatch
// v2.0: Oct 2026 redesign — licence address first, then the "do your letters show
//       this address?" question, then what counts. Same two outcomes as before.
// v1.1: Moved licence address display to just above action buttons for better UX
// v1.0: Initial implementation for address mismatch flow
// Created: January 2026

import React, { useState } from 'react';
import { StepShell, PHASE, Card, PrimaryButton, Note } from './ui/HireFormUI';

// What the insurer accepts. Kept in line with the list this page has always shown.
const ACCEPTED_DOCUMENTS = [
  'Gas, electric or water bill',
  'Bank or building society statement',
  'Credit card statement',
  'Council tax bill',
  'Mobile phone bill',
  'HMRC, DWP or NHS letter',
  'Other government letter',
];

const jobIdFromUrl = () => {
  try {
    return new URLSearchParams(window.location.search).get('job');
  } catch (e) {
    return null;
  }
};

const POAInstructionsPage = ({
  email,
  driverName,
  licenseAddress,
  onContinue,
  onBack,
  jobId
}) => {
  const [isLoading, setIsLoading] = useState(false);
  // Which answer was tapped — only so the right button shows it's busy.
  const [chosenMismatch, setChosenMismatch] = useState(null);
  const resolvedJobId = jobId || jobIdFromUrl();

  const handleContinueMatching = () => {
    setIsLoading(true);
    setChosenMismatch(false);
    // User says their documents match the licence address
    onContinue(false); // addressMismatch = false
  };

  const handleContinueMismatch = () => {
    setIsLoading(true);
    setChosenMismatch(true);
    // User says their documents show a different address
    onContinue(true); // addressMismatch = true
  };

  return (
    <StepShell jobId={resolvedJobId} phase={PHASE.ADDRESS}>
      <Card>
        <div>
          <h2 className="hf-h2">Two proofs of address</h2>
          <p className="hf-intro" style={{ marginTop: 6 }}>
            Our insurer needs to confirm where you live. A photo or a PDF is fine.
          </p>
        </div>

        {licenseAddress && (
          <div className="hf-inset">
            <p className="hf-small">Your licence says</p>
            <p className="hf-address">{licenseAddress}</p>
          </div>
        )}

        <div className="hf-question">
          <p className="hf-question-text">
            {licenseAddress
              ? 'Do your letters or bills show this address?'
              : 'Do your letters show the same address as your driving licence?'}
          </p>
          <div className="hf-actions">
            <PrimaryButton
              onClick={handleContinueMatching}
              disabled={isLoading}
              busy={isLoading && chosenMismatch === false}
              busyLabel="One moment…"
            >
              {licenseAddress ? 'Yes, they show this address' : 'Yes, the same address'}
            </PrimaryButton>
            <button
              type="button"
              className="hf-btn-secondary"
              onClick={handleContinueMismatch}
              disabled={isLoading}
            >
              {isLoading && chosenMismatch === true
                ? 'One moment…'
                : "No, I've moved — they show a new address"}
            </button>
          </div>
          <p className="hf-small">
            If your address is different, we'll also ask for a passport check.
          </p>
        </div>
      </Card>

      <Card className="hf-card-tight">
        <h3 className="hf-h3">What counts</h3>
        <p className="hf-body">
          Dated in the <strong>last 90 days</strong>, and from <strong>two different places</strong>{' '}
          (so not two statements from the same bank).
        </p>
        <div className="hf-chips">
          {ACCEPTED_DOCUMENTS.map((doc) => (
            <span key={doc} className="hf-chip">{doc}</span>
          ))}
        </div>
        <Note tone="amber">Insurance documents (car, home, travel) can't be used.</Note>
      </Card>

      {onBack && (
        <div className="hf-center-link">
          <button type="button" className="hf-link-btn" onClick={onBack} disabled={isLoading}>
            Back
          </button>
        </div>
      )}
    </StepShell>
  );
};

export default POAInstructionsPage;
